/**
 * 需要想定確定ダイアログ。
 *
 * 確定ボタン押下時に表示し、確定根拠 (rationale) の入力と一括確定を行う。
 * design 参照: design/demand/forecast-today.html #dft-confirm-modal
 */
import { useEffect, useState } from 'react'

import { Code, type ConnectError } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { Alert, Stack, Text, Textarea } from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { mapErrorToMessage } from '~/libs/mapConnectError'
import { resolveConfirmDetailedErrorMessage } from '~/pages/DemandForecastDaily/models/serverError'

/**
 * 確定失敗時の code ベース fallback 文言 (conventions.md §サーバーエラー:
 * 「状態の説明 + 次のアクション（または原因）」)。
 *
 * spec AC-DFC-006d [P0]: 確定できない原因が backend の
 * `google.rpc.ErrorInfo.reason` で分かる場合は
 * `resolveConfirmDetailedErrorMessage` が原因別の文言を返し、本マップは使わない。
 * ここに残るのは (a) reason を持たない別クラスのエラー (楽観ロック競合 =
 * Aborted / 権限 = PermissionDenied) と、(b) 原因を分類できなかった場合の最終
 * フォールバック (AC-DFC-006d 末尾「想定外の原因」= `notClassified`) のみ。
 * 原因が分からない catch-all 文言は AC-DFC-006d で廃止済み (issue #5433)。
 */
const CONFIRM_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.Aborted]: 'page:demandForecastDaily.confirm.serverError.aborted',
  [Code.PermissionDenied]:
    'page:demandForecastDaily.confirm.serverError.permissionDenied',
  // AC-DFC-006d 末尾「想定外の原因（原因判定ロジックで分類できないケース）」の
  // 最終フォールバック。業務エラーの 2 code (backend が reason を添付しなかった
  // = 分類できなかった経路) だけをここに集約する。技術的失敗 (Internal /
  // Unavailable 等) は業務上の「確定できない状態」ではないので、従来どおり
  // common:connectError.unknown の汎用文言へ落とす。
  //
  // [2026-09-29 デザイナー指摘] 文言から「画面を再読み込み」を外した。
  //  - **このダイアログは開いたまま**で、footer の「確定」をそのまま押し直せる。
  //    再読み込みは最も重い手段なのに先頭に置かれていた
  //  - ダイアログは確定根拠の Textarea を 2 つ持つ。再読み込みすると**入力中の
  //    根拠が消える**ので、案内として有害だった
  //  - 「入力値を確認」と言われても 48 コマの入力欄はダイアログの背後にあり、
  //    閉じないと見えない。何を確認すればよいかも書かれていなかった
  // 代わりに 再試行 → 閉じて 全コマの入力と未保存の変更を確認 → 管理者 の順で、
  // **分類できなかった時に実際に起こりうる原因 2 つ** (slotNotFilled /
  // draftNotSaved。backend が reason を添付できなかっただけで原因自体はこの
  // どちらかであることが多い) を名指しする。
  [Code.FailedPrecondition]:
    'page:demandForecastDaily.confirm.serverError.notClassified',
  [Code.InvalidArgument]:
    'page:demandForecastDaily.confirm.serverError.notClassified',
}

/**
 * 確定根拠入力欄のラベル (5 箇所で反復するため 1 部品に集約)。
 *
 * textStyle="label" (md/semibold) は design components.css:1007 `.form-label` =
 * md/600 が正。旧実装の fontSize="sm" (12px) からの意図的視覚変更
 * (12px→14px、design SoT 準拠、AdminOcctoMaster SubFormLabel と同型の是正、
 * 2026-07-12 ユーザー承認済み)。値・コメントの変更は本部品 1 箇所で行う。
 */
const RationaleLabel = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => <Text textStyle="label">{children}</Text>

type Props = {
  isOpen: boolean
  targetDateLabel: string
  /** AC-01 (spec AC-DFC-005): 確認表示するエリア名 (ケースのエリア由来) */
  areaLabel?: string
  onConfirm: (rationale: {
    curveRationale?: string
    maxPowerRationale?: string
    minPowerRationale?: string
    numericalBasis?: string
    freeNote?: string
  }) => void
  onCancel: () => void
  isLoading?: boolean
  /** 確定 mutation のエラー (未失敗なら null / undefined) */
  error?: ConnectError | null
}

export const ConfirmDemandDialog = ({
  isOpen,
  targetDateLabel,
  areaLabel,
  onConfirm,
  onCancel,
  isLoading = false,
  error = null,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [curveRationale, setCurveRationale] = useState('')
  const [maxPowerRationale, setMaxPowerRationale] = useState('')
  const [minPowerRationale, setMinPowerRationale] = useState('')
  const [numericalBasis, setNumericalBasis] = useState('')
  const [freeNote, setFreeNote] = useState('')

  // ダイアログを開くたびに入力をリセットする。 本コンポーネントは index.tsx で
  // 常時マウントされ open/close を Modal の isOpen で制御するため、 確定/キャンセル
  // 後の入力値が次回オープンに残ってしまう。 isOpen が true に変わった時のみ実行。
  useEffect(() => {
    if (isOpen) {
      setCurveRationale('')
      setMaxPowerRationale('')
      setMinPowerRationale('')
      setNumericalBasis('')
      setFreeNote('')
    }
  }, [isOpen])

  const handleConfirm = (): void => {
    onConfirm({
      curveRationale: curveRationale.trim() || undefined,
      maxPowerRationale: maxPowerRationale.trim() || undefined,
      minPowerRationale: minPowerRationale.trim() || undefined,
      numericalBasis: numericalBasis.trim() || undefined,
      freeNote: freeNote.trim() || undefined,
    })
  }

  // 権限が無いと拒否された確定は、 押し直しても同じ拒否になるだけなので押せなくする。
  // 理由は直上のエラー Alert が文で示す (BilateralTradeCsvUpload の削除権限拒否と同じ作り。
  // alert-placement §3)。 他の失敗 (楽観ロック競合・原因別の確定不可) は再試行で通りうるので止めない。
  const isPermissionDenied = error?.code === Code.PermissionDenied

  const footer = (
    <SaveCancelFooter
      isCancelDisabled={isLoading}
      isPending={isLoading}
      isSaveDisabled={isPermissionDenied}
      onCancel={onCancel}
      onSave={handleConfirm}
      saveLabel={t('page:demandForecastDaily.confirm.execute')}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onCancel}
      // 幅は UI 辞書 components.md `.ecl-modal` の 4 段 (既定 md)。 8080 `#dft-confirm-modal` は `ecl-modal--lg`
      size="lg"
      title={t('page:demandForecastDaily.confirm.title')}
    >
      <Stack gap={4}>
        {error !== null && error !== undefined && (
          <Alert
            description={
              // AC-DFC-006d: reason 由来の原因別文言を優先し、解決できない時
              // だけ code ベース fallback に落とす。
              resolveConfirmDetailedErrorMessage(error, t, targetDateLabel) ??
              mapErrorToMessage(error, t, CONFIRM_ERROR_KEYS)
            }
            status="error"
            title={t('page:demandForecastDaily.confirm.serverError.title')}
          />
        )}
        {/* 本文は m (14px)。 design プロト `#dft-confirm-modal` の同じ 1 文が
            `--font-size-md` 継承 = 14px / line-height 1.6 で、 それに揃える
            (2026-08-26 デザイナー指摘)。 以前は `fontSize="sm"` = **12px** で、
            ダイアログの主文が下の根拠記録ラベル (label = md/semibold) より小さかった。
            役割 role `body` (md / line-height 1.6) がこの値の canonical 定義
            (`~/constants/typography.ts`)。 生の fontSize 指定に戻さないこと。 */}
        <Text textStyle="body" color="Text.highEmphasis">
          {t('page:demandForecastDaily.confirm.description', {
            date: targetDateLabel,
          })}
        </Text>

        {/* AC-DFC-005: 確認するのは対象期間・エリアまで。 **確定時刻は出さない** —
            本ダイアログは確定「前」 の確認画面で、 この時点の時刻は確定時刻ではない
            (現在時刻を「確定時刻」 として出すと「もう確定済み」 と誤認させる。
            issue #6300、 2026-08-25 PdM 判断)。 実際の確定時刻は確定完了後の
            ConfirmResultDialog (AC-DFC-006b) が出す。 */}
        {/* エリアは他のフォーム項目と同じ DS の form label で見出しを付ける
            (2026-08-26 デザイナー指摘)。 以前は素の `<Text fontSize="sm">` +
            mid-emphasis で、 同じダイアログ内の根拠記録ラベル (label = md/semibold /
            high-emphasis) と字送りも色も揃っていなかった。
            入力欄ではないので `FieldRoot` は使わず、 ラベルの見た目だけを揃える。 */}
        <Stack gap={1}>
          <Text textStyle="label" color="Text.highEmphasis">
            {t('page:demandForecastDaily.confirm.areaLabel')}
          </Text>
          <Text fontVariantNumeric="tabular-nums">{areaLabel ?? '—'}</Text>
        </Stack>

        {/* AC-02: 需要カーブ採用根拠 */}
        <Stack gap={1}>
          <RationaleLabel>
            {t('page:demandForecastDaily.confirm.curveRationale')}
          </RationaleLabel>
          <Textarea
            maxLength={100}
            onChange={(e) => {
              setCurveRationale(e.target.value)
            }}
            placeholder={t(
              'page:demandForecastDaily.confirm.rationalePlaceholder',
            )}
            rows={2}
            size="sm"
            value={curveRationale}
          />
        </Stack>

        {/* AC-02: 最大電力の根拠 */}
        <Stack gap={1}>
          <RationaleLabel>
            {t('page:demandForecastDaily.confirm.maxPowerRationale')}
          </RationaleLabel>
          <Textarea
            maxLength={100}
            onChange={(e) => {
              setMaxPowerRationale(e.target.value)
            }}
            placeholder={t(
              'page:demandForecastDaily.confirm.rationalePlaceholder',
            )}
            rows={2}
            size="sm"
            value={maxPowerRationale}
          />
        </Stack>

        {/* AC-02: 最小電力の根拠 */}
        <Stack gap={1}>
          <RationaleLabel>
            {t('page:demandForecastDaily.confirm.minPowerRationale')}
          </RationaleLabel>
          <Textarea
            maxLength={100}
            onChange={(e) => {
              setMinPowerRationale(e.target.value)
            }}
            placeholder={t(
              'page:demandForecastDaily.confirm.rationalePlaceholder',
            )}
            rows={2}
            size="sm"
            value={minPowerRationale}
          />
        </Stack>

        {/* AC-02: 数値的根拠 (文言は spec 準拠のため変更なし) */}
        <Stack gap={1}>
          <RationaleLabel>
            {t('page:demandForecastDaily.confirm.numericalBasis')}
          </RationaleLabel>
          <Textarea
            maxLength={100}
            onChange={(e) => {
              setNumericalBasis(e.target.value)
            }}
            placeholder={t(
              'page:demandForecastDaily.confirm.rationalePlaceholder',
            )}
            rows={2}
            size="sm"
            value={numericalBasis}
          />
        </Stack>

        {/* AC-02: 自由メモ */}
        <Stack gap={1}>
          <RationaleLabel>
            {t('page:demandForecastDaily.confirm.freeNote')}
          </RationaleLabel>
          <Textarea
            maxLength={100}
            onChange={(e) => {
              setFreeNote(e.target.value)
            }}
            placeholder={t(
              'page:demandForecastDaily.confirm.rationalePlaceholder',
            )}
            rows={2}
            size="sm"
            value={freeNote}
          />
        </Stack>
      </Stack>
    </Modal>
  )
}
